import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useDebouncedValue } from "apps/commons/hooks/useDebouncedValue";
import type { PropertyData } from "libs/codegen/property-onboarding";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import type { GooglePlaceResult } from "../types";
import { useMarkerController } from "../useMarkerController";
import { parseAddressComponents } from "./parseAddressComponents";
import { parseAddressWithoutRegion } from "./parseAddressWithoutRegion";
import { useSearchInputAtomState } from "./searchInputAtom";

export const useMapSearchHandler = () => {
    const [input, setInput] = useSearchInputAtomState();
    const debouncedValue = useDebouncedValue(input, 300);

    const map = useMap();
    const placesLib = useMapsLibrary("places");
    const { setValue } = useFormContext<PropertyData>();

    const { movePin } = useMarkerController();
    const [places, setPlaces] = useState<GooglePlaceResult[]>([]);

    useEffect(() => {
        if (debouncedValue.trim() === "") return setPlaces([]);
        if (!placesLib || !map || debouncedValue.trim() === "") return;

        const autocompleteService = new placesLib.AutocompleteService();
        autocompleteService.getPlacePredictions(
            {
                input: debouncedValue,
                types: ["geocode", "establishment"],
            },
            (predictions, status) => {
                if (status === placesLib.PlacesServiceStatus.OK && predictions) {
                    // Convert predictions to places using getDetails
                    const placesService = new placesLib.PlacesService(map);
                    Promise.all(
                        predictions.slice(0, 5).map(
                            (prediction) =>
                                new Promise<GooglePlaceResult>((resolve) => {
                                    placesService.getDetails(
                                        { placeId: prediction.place_id },
                                        (result, detailStatus) => {
                                            if (detailStatus === placesLib.PlacesServiceStatus.OK && result) {
                                                resolve(result as GooglePlaceResult);
                                            }
                                        },
                                    );
                                }),
                        ),
                    ).then((results) => setPlaces(results));
                } else if (status === placesLib.PlacesServiceStatus.ZERO_RESULTS) setPlaces([]);
            },
        );
    }, [debouncedValue, map, placesLib]);

    const handleSelectPlace = (place: GooglePlaceResult) => {
        movePin(place.geometry.location.toJSON());
        map.setZoom(15);

        const parsedAddress = parseAddressComponents(place.address_components);
        const sanitizedAddress = parseAddressWithoutRegion(place.adr_address);

        setValue("postalCode", parsedAddress.postalCode ?? "");
        setValue("streetAddress", sanitizedAddress);
        setValue("floorAndUnit", place.name ?? "");
    };

    return {
        input,
        setInput,
        debouncedValue,
        items: places,
        handleSelectPlace,
    };
};
